import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { selectData, selectById, selectIsLoading } from '../selectors.ts';
import { useAttach } from '../useAttach';

import type { MoodProps } from './MoodsStore';
import { moodsStore, type MoodsStoreState } from './MoodsStore';

export function useMoodsData() {
  useAttach(moodsStore, 'useMoodsData');
  return useStoreWithEqualityFn(moodsStore.store, selectData, shallow);
}
useMoodsData.store = moodsStore;

export function useMoodsById() {
  useAttach(moodsStore, 'useMoodsById');
  return useStoreWithEqualityFn(moodsStore.store, selectById, shallow);
}
useMoodsById.store = moodsStore;

export function useMoodsByZoneId() {
  useAttach(moodsStore, 'useMoodsByZoneId');

  const moods = moodsStore.store(selectById, shallow);

  const byZoneId = useMemo(() => {
    const byZoneId: Record<string, Record<string, MoodProps>> = {};

    for (const mood of Object.values(moods.byId ?? {})) {
      byZoneId[mood.zone] = byZoneId[mood.zone] ?? {};
      byZoneId[mood.zone][mood.id] = mood;
    }

    return byZoneId;
  }, [moods.byId]);

  return {
    ...moods,
    byZoneId,
  };
}

export function useMood({ moodId }: { moodId: string }) {
  useAttach(moodsStore, 'useMood');

  const select = useCallback(
    (state: MoodsStoreState) => {
      return {
        mood: state.byId?.[moodId] ?? null,
        moodInstance: state.data?.[moodId] ?? null,
      };
    },
    [moodId],
  );

  return useStoreWithEqualityFn(moodsStore.store, select, shallow);
}
useMood.store = moodsStore;

export function useMoodsAttach() {
  useAttach(moodsStore, 'useMoodsAttach');
}
useMoodsAttach.store = moodsStore;

export function useMoodsLoading() {
  useAttach(moodsStore, 'useMoodsLoading');
  return useStoreWithEqualityFn(moodsStore.store, selectIsLoading, Object.is);
}
useMoodsLoading.store = moodsStore;
